<template>
  <v-dialog
    v-if="state.visible"
    :model-value="state.visible"
    max-width="520"
    persistent
    :aria-label="title"
    data-cy="accounting-mode-change-dialog"
  >
    <v-card>
      <v-card-title data-cy="accounting-mode-change-title">
        {{ title }}
      </v-card-title>

      <v-card-text v-if="invoicesBlock">
        <div class="mb-4" data-cy="accounting-mode-change-invoice-message">
          <LlIcon icon="mdi-alert" color="warning" class="mr-2" />
          <span class="text-body-2">
            One or more {{ matterTermLower }}s belonging to this client has an
            unpaid or partially paid invoice in the Manage Invoices page. Please
            move invoice{{ state.invoices.length > 1 ? 's' : '' }}
            ({{ invoiceReferenceList }}) back to Drafts before converting this
            account to {{ newModeHyphenated }}.
          </span>
        </div>
      </v-card-text>

      <v-card-text v-else>
        <div class="mb-4" data-cy="accounting-mode-change-message">
          <LlIcon icon="mdi-alert" color="warning" class="mr-2" />
          <span class="text-body-2">
            <template v-if="canResetAll">
              Switching from <strong>{{ oldModeLabel }}</strong> to
              <strong>{{ newModeLabel }}</strong> resets
              {{ settlementNoun }}.
            </template>
            <template v-else>
              This client's QuickBooks accounting setting can't be changed from
              <strong>{{ oldModeLabel }}</strong> to
              <strong>{{ newModeLabel }}</strong> right now —
              {{ blockingSummary }}.
            </template>
            <template v-if="state.settlements.length > 1">
              The setting applies to every {{ matterTermLower }} under this
              client, so they have to be reset together.
            </template>
          </span>
        </div>

        <div class="mt-4">
          <div class="font-weight-medium mb-2">{{ listLabel }}</div>
          <ul class="ml-4" data-cy="accounting-mode-change-matters">
            <li
              v-for="settlement in state.settlements"
              :key="settlement.settlementId"
              class="mb-1"
            >
              {{ settlement.matterName }}
              <div
                v-if="!settlement.canReset && settlement.resetBlockedReason"
                class="text-caption text-error d-flex align-start gap-1"
                data-cy="accounting-mode-change-reset-blocked"
              >
                <LlIcon
                  icon="mdi-lock"
                  color="error"
                  size="13"
                  class="flex-shrink-0 mt-1"
                />
                {{ settlement.resetBlockedReason }}
              </div>
            </li>
          </ul>
        </div>

        <!-- The cost of the reset, stated above the button that causes it.
             Wording mirrors the Settlement tab's own Unlock & Reset confirm. -->
        <p
          v-if="canResetAll"
          class="text-body-2 mt-4 mb-0"
          data-cy="accounting-mode-change-consequence"
        >
          Resetting returns
          {{ state.settlements.length > 1 ? 'each settlement' : 'the settlement' }}
          to Step 1. Confirmed deposits, the fee calculation and any generated
          statement are cleared.
        </p>

        <p class="text-caption text-grey-darken-1 mt-4 mb-0">
          If invoices were already created in QuickBooks at the
          {{ oldModeLabel }} level — especially under the "Client pays expenses
          as they go" arrangement — they may need to be manually recreated at
          the {{ newModeLabel }} level. Email
          <a href="mailto:support@myleanlaw.com">support@myleanlaw.com</a>
          if you'd like help.
        </p>
      </v-card-text>

      <v-card-actions class="pa-4">
        <template v-if="showResetOption">
          <LlSecondaryButton
            :disabled="state.resetting"
            data-cy="accounting-mode-change-acknowledge"
            @click="handleDismiss"
          >
            Cancel
          </LlSecondaryButton>
          <v-spacer />
          <!-- `error`, not green: this destroys confirmed work. LlListConfirmationDialog's
               own default for a destructive confirm is the same token. -->
          <LlSecondaryButton
            color="error"
            variant="outlined"
            :loading="state.resetting"
            :disabled="state.resetting"
            data-cy="accounting-mode-change-reset"
            @click="handleReset"
          >
            {{ resetButtonLabel }}
          </LlSecondaryButton>
        </template>
        <template v-else>
          <v-spacer />
          <LlSecondaryButton
            variant="outlined"
            data-cy="accounting-mode-change-acknowledge"
            @click="handleDismiss"
          >
            OK
          </LlSecondaryButton>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import type { BlockingSettlement } from '@/models/settlementTypes';
import type { BlockingInvoice } from '@/models/invoiceTypes';

/** Past this many the list stops informing and starts filling the dialog. */
const MAX_LISTED_INVOICES = 5;

interface OpenParams {
  /** `true` when the mode the settlement was started under was Client Accounting. */
  oldModeIsClientAccounting: boolean;
  /** `true` for the mode the user just attempted to select on the QuickBooks tab. */
  newModeIsClientAccounting: boolean;
  /** The client the mode belongs to — the reset is client-scoped, not per settlement. */
  clientId: string;
  /** Every settlement under the client blocking the change (BR-16/BR-17). */
  settlements: BlockingSettlement[];
  /**
   * Unpaid or partly paid invoices under the client. Non-empty puts the
   * dialog in its refusal mode — no reset is offered, because none would help.
   */
  invoices?: BlockingInvoice[];
  /** `me.terms.Matter` — firms rename "Matter" and the toggle already honours it. */
  matterTerm?: string;
}

/**
 * Mounted imperatively by accountingModeGuard.ts via `vm.open(params)` into a host
 * it creates, because the callers are AngularJS controllers with no Vue parent to
 * hold a template ref. `open()` resolves `true` once Unlock & Reset succeeds,
 * `false` on dismiss.
 */
const state = reactive({
  visible: false,
  oldModeIsClientAccounting: false,
  newModeIsClientAccounting: false,
  clientId: null as string | null,
  settlements: [] as BlockingSettlement[],
  invoices: [] as BlockingInvoice[],
  resetting: false,
  matterTerm: 'Matter',
});

const settlementStore = useSettlementStore();

let resolveOpen: ((didReset: boolean) => void) | null = null;
// Monotonic guard: a reset already in flight must not resolve a later open().
let openToken = 0;

const modeLabel = (isClientAccounting: boolean) =>
  isClientAccounting ? 'Client Accounting' : `${state.matterTerm} Accounting`;

const oldModeLabel = computed(() => modeLabel(state.oldModeIsClientAccounting));
const newModeLabel = computed(() => modeLabel(state.newModeIsClientAccounting));

const matterTermLower = computed(() => state.matterTerm.toLowerCase());

/**
 * The invoice refusal outranks the settlement branch: it admits no remedy inside the
 * dialog, so nothing below it should offer one. The guard only fills `invoices` when
 * the user is moving *into* Client Accounting, so this cannot fire the other way.
 */
const invoicesBlock = computed(() => state.invoices.length > 0);

/**
 * The mode being moved to, in the hyphenated form the refusal sentence reads in
 * ("…converting this account to client-accounting"). Lower-cased and hyphenated
 * rather than reusing `newModeLabel`, which is title-cased for use as a noun
 * elsewhere in this dialog and would read wrong mid-sentence.
 */
const newModeHyphenated = computed(() =>
  state.newModeIsClientAccounting
    ? 'client-accounting'
    : `${matterTermLower.value}-accounting`,
);

/**
 * The invoice numbers, capped — a client with forty open invoices would otherwise
 * push the action buttons off the bottom of the dialog and bury the instruction the
 * user actually has to follow. The overflow is counted rather than dropped silently,
 * because the remedy is "move them all back to Drafts" and a truncated list that
 * looked complete would send them back here.
 */
const invoiceReferenceList = computed(() => {
  const references = state.invoices.map(
    (invoice) => invoice.reference || 'no number',
  );
  const shown = references.slice(0, MAX_LISTED_INVOICES).join(', ');
  const remaining = references.length - MAX_LISTED_INVOICES;
  return remaining > 0 ? `${shown} and ${remaining} more` : shown;
});

/**
 * One blocked sibling refuses the whole change. The mode is client-wide, so
 * resetting only the resettable ones would leave the client half-migrated —
 * worse than refusing, because the surviving snapshots were derived under a mode
 * that no longer applies.
 */
const canResetAll = computed(
  () =>
    state.settlements.length > 0 &&
    state.settlements.every((settlement) => settlement.canReset),
);

/**
 * The single predicate behind both the Reset button and the title, so the two can
 * never disagree — the bug the title comment below records was exactly that.
 * `canResetAll` is already false in the invoice branch, since it arrives with no
 * settlements; the explicit term keeps that true if a caller ever sends both.
 */
const showResetOption = computed(() => canResetAll.value && !invoicesBlock.value);

const settlementNoun = computed(() =>
  state.settlements.length > 1
    ? `the settlements on ${state.settlements.length} ${matterTermLower.value}s`
    : `this ${matterTermLower.value}'s settlement`,
);

/**
 * Titled for what the user can do, not only for what they can't. The refusal
 * wording is correct when nothing can be reset; keeping it over a live Reset
 * button told the user "no" directly above the button that means "yes".
 */
const title = computed(() =>
  showResetOption.value
    ? `Reset ${state.settlements.length > 1 ? 'settlements' : 'settlement'} to change this setting`
    : "Can't Change Accounting Setting",
);

const listLabel = computed(() =>
  canResetAll.value
    ? `${state.settlements.length > 1 ? 'Settlements' : 'Settlement'} to reset`
    : `${state.settlements.length > 1 ? 'Settlements' : 'Settlement'} in progress`,
);

const blockingSummary = computed(() =>
  state.settlements.length > 1
    ? `these ${matterTermLower.value}s have settlements in progress`
    : `this ${matterTermLower.value} has a settlement in progress`,
);

const resetButtonLabel = computed(() =>
  state.settlements.length > 1 ? 'Reset Settlements' : 'Reset Settlement',
);

function open(params: OpenParams): Promise<boolean> {
  // A prior open() is still pending — settle it as superseded so its caller
  // never hangs on a promise that would otherwise be silently overwritten.
  resolveOpen?.(false);
  openToken += 1;

  state.oldModeIsClientAccounting = params.oldModeIsClientAccounting;
  state.newModeIsClientAccounting = params.newModeIsClientAccounting;
  state.clientId = params.clientId;
  state.settlements = params.settlements ?? [];
  state.invoices = params.invoices ?? [];
  state.matterTerm = params.matterTerm || 'Matter';
  state.resetting = false;
  state.visible = true;
  return new Promise((resolve) => {
    resolveOpen = resolve;
  });
}

function handleDismiss(): void {
  state.visible = false;
  resolveOpen?.(false);
  resolveOpen = null;
}

async function handleReset(): Promise<void> {
  if (!state.clientId || !canResetAll.value) return;
  // Capture this open()'s resolver and token: if it is superseded mid-request,
  // the success must not resolve whichever caller happens to be current.
  const resolveThis = resolveOpen;
  const token = openToken;
  state.resetting = true;
  try {
    await settlementStore.resetSettlementsForClient(state.clientId);
    resolveThis?.(true);
    if (token !== openToken) return;
    state.visible = false;
    resolveOpen = null;
  } catch {
    // Error already surfaced by the store (useSnackbarHandler.handleError).
  } finally {
    if (token === openToken) state.resetting = false;
  }
}

/**
 * The host app can be unmounted with a dialog still open — accountingModeGuard.ts
 * unmounts this island when Angular destroys the controller that created it, which
 * happens on navigation. Nothing else settles `open()`, so without this the caller's
 * `await openDialog(...)` never returns: its frame, holding the settlement
 * response, is retained for the rest of the session and its `.catch` never runs.
 *
 * Resolve `false` rather than rejecting — "the user did not reset" is the truth
 * here, and rejecting would surface a toast on whatever page they navigated to.
 * Bumping the token also marks any in-flight reset as superseded, so it does not
 * write state back into a component that is gone.
 */
onBeforeUnmount(() => {
  openToken += 1;
  resolveOpen?.(false);
  resolveOpen = null;
});

defineExpose({ open });
</script>
